import { Injectable } from '@angular/core';
import { Observable, of, switchMap, take } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { ApiService } from '@core/services/api.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { LoggerService } from '@core/services/logger/logger.service';
import { LAB_BROKER_TIMEOUTS } from '../consts';

export interface CachedToken {
	accessToken: string;
	tokenType: string;
	expiresAt: number;
}

@Injectable({ providedIn: 'root' })
export class CustomRxTokenService {
	private readonly moduleName = 'CustomRxTokenService';
	private cachedToken: CachedToken | null = null;
	private lastFetchParams: { labCompanyId: number; doctorCompanyId: number } | null = null;

	constructor(
		private readonly apiService: ApiService,
		private readonly shellQuery: ShellQuery,
		private readonly logger: LoggerService
	) {}

	getToken(labCompanyId: number, doctorCompanyId: number): Observable<CachedToken> {
		if (!labCompanyId || !doctorCompanyId) {
			throw new Error('labCompanyId and doctorCompanyId are required');
		}

		if (this.cachedToken && !this.isTokenExpiringSoon()) {
			this.logger.debug('CustomRxTokenService: cache hit', { module: this.moduleName });

			return of(this.cachedToken);
		}

		return this.fetchToken(labCompanyId, doctorCompanyId);
	}

	refreshToken(): Observable<CachedToken> {
		if (!this.lastFetchParams) {
			throw new Error('No fetch params available for refresh');
		}

		this.logger.debug('CustomRxTokenService: initiating token refresh', { module: this.moduleName });
		this.cachedToken = null;

		return this.fetchToken(this.lastFetchParams.labCompanyId, this.lastFetchParams.doctorCompanyId);
	}

	isTokenExpiringSoon(): boolean {
		if (!this.cachedToken) {
			return false;
		}

		return this.cachedToken.expiresAt - Date.now() <= LAB_BROKER_TIMEOUTS.TokenExpirationBufferMs;
	}

	clearCache(): void {
		this.cachedToken = null;
		this.logger.debug('CustomRxTokenService: token cache cleared', { module: this.moduleName });
	}

	private fetchToken(labCompanyId: number, doctorCompanyId: number): Observable<CachedToken> {
		return this.shellQuery.rxappBffUrl$.pipe(
			take(1),
			switchMap(bffUrl => {
				if (!bffUrl) {
					throw new Error('rxappBffUrl is not initialized');
				}

				return this.apiService
					.request({
						selector: 'getCustomRxToken',
						baseUrl: bffUrl,
						queryParams: { labCompanyId, doctorCompanyId }
					})
					.pipe(
						map((response: { AccessToken: string }) => {
							let expiresAt = Date.now() + LAB_BROKER_TIMEOUTS.TokenExpirationBufferMs;

							try {
								const decodedToken: { exp: number } = jwtDecode(response?.AccessToken);

								if (decodedToken?.exp >= 0) {
									expiresAt = decodedToken.exp;
								}
							} catch {}

							const token: CachedToken = {
								accessToken: response?.AccessToken,
								tokenType: 'Bearer',
								expiresAt
							};

							this.cachedToken = token;
							this.lastFetchParams = { labCompanyId, doctorCompanyId };
							this.logger.info('CustomRxTokenService: token fetched successfully', {
								module: this.moduleName,
								extendedParameters: { expiresAt: String(token.expiresAt) }
							});

							return token;
						}),
						catchError(err => {
							this.logger.warn('CustomRxTokenService: token fetch failed', {
								module: this.moduleName,
								error: err instanceof Error ? err : new Error(String(err)),
								extendedParameters: {
									labCompanyId: String(labCompanyId),
									doctorCompanyId: String(doctorCompanyId)
								}
							});
							throw Object.assign(err instanceof Error ? err : new Error(String(err)), {
								isTokenFetchError: true
							});
						})
					);
			})
		);
	}
}
